import { mui } from "@/mui";
import type { ReactNode } from "react";
import { useState, useEffect } from "react";
import { useMobyDB, useMobyNode, useSession } from "./provider";
import { toast } from "sonner";
import type { ContactEntity } from "@/moby/db";
import { getPeerAvatarColor, renderPeerAvatarEmoji } from "@/lib/peer-profile-utils";
import { ImageIcon, Trash2, ShieldOff, Flag } from "lucide-react";
import { useLocation } from "wouter";

interface EditContactDialogProps {
  contact: ContactEntity;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onConversationDeleted?: () => void;
}

export function EditContactDialog({ contact, children, open: controlledOpen, onOpenChange, onConversationDeleted }: EditContactDialogProps) {
  const db = useMobyDB();
  const node = useMobyNode();
  const session = useSession();
  const [location, navigate] = useLocation();
  const [internalOpen, setInternalOpen] = useState(false);
  const [nickname, setNickname] = useState(contact.nickname || "");
  const [renderImages, setRenderImages] = useState(
    () => localStorage.getItem(`moby_peer_renderImages_${contact.address}`) === 'true'
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Re-sync renderImages from localStorage whenever the dialog opens.
  // Prevents stale toggle state when the component instance persists across open/close cycles.
  useEffect(() => {
    if (controlledOpen || internalOpen) {
      setRenderImages(localStorage.getItem(`moby_peer_renderImages_${contact.address}`) === 'true');
    }
  }, [controlledOpen, internalOpen, contact.address]);
  
  // Use controlled state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const handleSave = async () => {
    if (isSaving) return;

    const trimmedNickname = nickname.trim();

    // Validation
    if (trimmedNickname && trimmedNickname.length > 24) {
      toast.error("Nickname must be less than 24 characters.");
      return;
    }

    if (trimmedNickname && !/^[a-zA-Z0-9](?:[a-zA-Z0-9._]|(?:\s[a-zA-Z0-9]))*[a-zA-Z0-9]$/.test(trimmedNickname)) {
      toast.error("Name may only contain letters, numbers, spaces, and underscores.");
      return;
    }

    setIsSaving(true);
    try {
      // Update contact nickname (local only - not transmitted)
      await db.contacts.update(contact.address, {
        nickname: trimmedNickname || undefined,
      });

      // Save per-peer image rendering preference
      if (renderImages) {
        localStorage.setItem(`moby_peer_renderImages_${contact.address}`, 'true');
      } else {
        localStorage.removeItem(`moby_peer_renderImages_${contact.address}`);
      }

      // Dispatch event so chat panel + AttachmentCards update reactively
      window.dispatchEvent(new CustomEvent('moby-render-images-changed', {
        detail: { address: contact.address, enabled: renderImages },
      }));

      console.log('[EditContactDialog] Saved render setting', {
        peer: contact.address.slice(0, 8),
        renderImages,
        key: `moby_peer_renderImages_${contact.address}`,
        stored: localStorage.getItem(`moby_peer_renderImages_${contact.address}`),
      });
      toast.success("Contact updated successfully!");
      setOpen(false);

      // Emit event for UI updates
      window.dispatchEvent(new CustomEvent("contact-updated", {
        detail: { contactAddress: contact.address }
      }));
    } catch (error) {
      console.error("Failed to update contact:", error);
      toast.error("Failed to update contact");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to original values
    setNickname(contact.nickname || "");
    setRenderImages(localStorage.getItem(`moby_peer_renderImages_${contact.address}`) === 'true');
    setOpen(false);
  };

  return (
    <mui.Dialog.Root open={open} onOpenChange={setOpen}>
      {children}
      <mui.Dialog.Portal>
        <mui.Dialog.Backdrop onClick={(e) => e.stopPropagation()} />
        <mui.Dialog.Popup className="max-w-md max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
          <mui.Dialog.Title>Edit Contact</mui.Dialog.Title>
          <div className="bg-tertiary-container text-on-tertiary-container p-3 rounded-lg text-body-sm mt-2">
            <div>
              These settings are stored locally on your device and are not shared with other peers. 
              The peer's display name ("{contact.card?.name || 'their name'}") will still be visible to others.
            </div>
          </div>

          <div className="space-y-6 mt-6 overflow-y-auto flex-1 min-h-0 pr-1">
            {/* Contact Info Display */}
            <div className="flex items-center gap-4 p-3 bg-surface-container-high rounded-lg">
              <div 
                className="grid size-12 place-items-center rounded-full text-white"
                style={{ backgroundColor: getPeerAvatarColor(contact.custom?.profile) }}
              >
                <div className="text-title-sm">{contact.getInitials()}</div>
                {renderPeerAvatarEmoji(contact.custom?.profile, "sm")}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-body-md font-medium truncate">
                  {contact.card?.name || `User-${contact.address.substring(0, 8)}`}
                </div>
                <div className="text-body-sm text-on-surface-variant truncate font-mono text-xs">
                  {contact.address}
                </div>
              </div>
            </div>

            {/* Nickname Input */}
            <div className="relative">
              <mui.Input
                filled
                type="text"
                name="nickname"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder={contact.card?.name || contact.address.substring(0, 8)}
                className="rounded-t-extra-small"
                autoFocus
              />
              <mui.FloatingLabel htmlFor="nickname">
                Local Nickname
              </mui.FloatingLabel>
            </div>

            <mui.InputDescription>
              Set a custom nickname for this contact. It will only be visible to you and won't affect what other peers see.
            </mui.InputDescription>

            {/* Peer Settings */}
            <div>
              <div className="text-label-lg text-on-surface mb-2">Peer Settings</div>
              <label className="flex items-center justify-between p-3 bg-surface-container-high rounded-lg cursor-pointer hover:bg-surface-container-highest transition-colors">
                <div className="flex items-center gap-3">
                  <ImageIcon className="h-5 w-5 text-on-surface-variant" />
                  <div>
                    <div className="text-body-md text-on-surface">Render Images</div>
                    <div className="text-body-sm text-on-surface-variant">Show image attachments inline in chat</div>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={renderImages}
                  onChange={(e) => setRenderImages(e.target.checked)}
                  className="h-5 w-5 rounded accent-primary cursor-pointer"
                />
              </label>
            </div>

            {/* Actions */}
            <div>
              <div className="text-label-lg text-error mb-2">Actions</div>
              <div className="space-y-2">
                {/* Delete Conversation */}
                <button
                  disabled={isDeleting}
                  onClick={async () => {
                    const confirmed = window.confirm(
                      `Delete conversation with ${contact.card?.name || contact.address.substring(0, 8)}?\n\nThis will remove all messages and associated files locally. The contact will be preserved.`
                    );
                    if (!confirmed) return;
                    setIsDeleting(true);
                    try {
                      await db.clearConversationHistory(contact.address);
                      toast.success('Conversation history cleared.');
                      setOpen(false);
                      onConversationDeleted?.();
                      if (location === `/chats/${contact.address}`) {
                        navigate('/chats');
                      }
                    } catch (error) {
                      console.error('[EditContactDialog] Failed to clear history:', error);
                      toast.error('Failed to delete conversation.');
                    } finally {
                      setIsDeleting(false);
                    }
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-lg text-left hover:bg-error/8 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="h-5 w-5 text-error shrink-0" />
                  <div>
                    <div className="text-body-md text-error">Delete Conversation</div>
                    <div className="text-body-sm text-on-surface-variant">Clear all messages and files. Contact is preserved.</div>
                  </div>
                </button>

                {/* Block User */}
                <button
                  disabled={isDeleting}
                  onClick={async () => {
                    const confirmed = window.confirm(
                      `Block and remove ${contact.card?.name || contact.address.substring(0, 8)}?\n\nThis will permanently remove the contact, all messages, and prevent future communication. This action is broadcast to the peer.`
                    );
                    if (!confirmed) return;
                    setIsDeleting(true);
                    try {
                      node.markPeerAsDeleted(contact.address);
                      await db.deleteContact(contact.address, session?.address || 'unknown', session?.address || 'unknown');
                      toast.success('Contact blocked and removed.');
                      setOpen(false);
                      onConversationDeleted?.();
                      if (location === `/chats/${contact.address}`) {
                        navigate('/chats');
                      }
                    } catch (error) {
                      console.error('[EditContactDialog] Failed to block contact:', error);
                      toast.error('Failed to block contact.');
                    } finally {
                      setIsDeleting(false);
                    }
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-lg text-left hover:bg-error/8 transition-colors disabled:opacity-50"
                >
                  <ShieldOff className="h-5 w-5 text-error shrink-0" />
                  <div>
                    <div className="text-body-md text-error">Block User</div>
                    <div className="text-body-sm text-on-surface-variant">Remove contact and block all communication.</div>
                  </div>
                </button>

                {/* Report User */}
                <button
                  disabled={isDeleting}
                  onClick={() => {
                    window.open(`mailto:support@mobynet.io?subject=Report%20User%3A%20${encodeURIComponent(contact.address.substring(0, 16))}...&body=Reported%20address%3A%20${encodeURIComponent(contact.address)}%0A%0ADescription%20of%20issue%3A%0A`, '_blank');
                    toast.info('Opening email to support@mobynet.io');
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-lg text-left hover:bg-on-surface/8 transition-colors disabled:opacity-50"
                >
                  <Flag className="h-5 w-5 text-on-surface-variant shrink-0" />
                  <div>
                    <div className="text-body-md text-on-surface">Report User</div>
                    <div className="text-body-sm text-on-surface-variant">Report to support@mobynet.io</div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-outline-variant shrink-0">
            <mui.Dialog.Close>
              <mui.Button
                variant="surface"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </mui.Button>
            </mui.Dialog.Close>
            <mui.Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </mui.Button>
          </div>
        </mui.Dialog.Popup>
      </mui.Dialog.Portal>
    </mui.Dialog.Root>
  );
}
